import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import {
  handleInput,
  handleSubmit,
  transformUnderscoreToCamel,
} from "../../../../services/utils/processors";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Input from "../../../../components/form/input";
import InputArea from "../../../../components/form/inputArea";
import CountryDropdown from "../../../../components/form/country-dropdown";
import styled from "styled-components";

// -- Styled Components for Redesign --
const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color, #333);
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const LinkRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: stretch;
  background: var(--surface-ground, #f9fafb);
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e5e7eb);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;

const ProfileForm = ({
  defaultData = {},
  loading = false,
  onSave,
  onBack,
  editMode = false,
}) => {
  const [info, setInfo] = useState({ links: [] });
  const [infoError, setInfoError] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (defaultData) {
      const transformed = transformUnderscoreToCamel(defaultData);

      const linksArray =
        transformed.links &&
        typeof transformed.links === "object" &&
        !Array.isArray(transformed.links)
          ? Object.entries(transformed.links).map(([title, url]) => ({
              title,
              url,
            }))
          : Array.isArray(transformed.links)
            ? transformed.links
            : [];

      setInfo({ ...transformed, links: linksArray });
    }
  }, [defaultData]);

  const schema = Yup.object({
    username: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email format"),
    phone: Yup.string()
      .matches(/\+?[1-9]\d{1,14}$/, "Invalid phone number")
      .required("Phone number is required"),
    bio: Yup.string().required("Bio is required"),
    country: Yup.string().required("Country is required"),
    links: Yup.array().of(
      Yup.object({
        title: Yup.string().required("Platform is required"),
        url: Yup.string().required("URL is required"),
      }),
    ),
  });

  const handleBack = () => navigate("/onboarding/choose-profile");

  const handleSubmitData = () => {
    handleSubmit(info, schema, setInfoError, (data) => {
      const linksObj = Object.fromEntries(
        info.links
          .filter(
            ({ title, url }) => title?.trim() !== "" && url?.trim() !== "",
          )
          .map(({ title, url }) => [title.trim(), url.trim()]),
      );
      onSave({ ...data, links: linksObj });
    });
  };

  const handleLinkChange = (index, field, value) => {
    setInfo((prev) => {
      const links = [...prev.links];
      links[index][field] = value;
      return { ...prev, links };
    });
  };

  const addLink = () => {
    setInfo((prev) => ({
      ...prev,
      links: [...prev.links, { title: "", url: "" }],
    }));
  };

  const removeLink = (index) => {
    setInfo((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  return (
    <FormContainer>
      <div>
        <SectionTitle>Personal Information</SectionTitle>
        <Grid>
          <div>
            <Input
              value={info.username || ""}
              label="Full Name"
              width="100%"
              onChange={(val) =>
                handleInput(val, "username", setInfo, setInfoError, schema)
              }
              error={infoError.username}
            />
          </div>
          <div>
            <Input
              disabled
              value={info.email || ""}
              label="Email"
              type="email"
              width="100%"
              onChange={(val) =>
                handleInput(val, "email", setInfo, setInfoError, schema)
              }
              error={infoError.email}
            />
          </div>
          <div>
            <Input
              value={info.phone || ""}
              label="Phone Number"
              width="100%"
              onChange={(val) =>
                handleInput(val, "phone", setInfo, setInfoError, schema)
              }
              error={infoError.phone}
            />
          </div>
          <div>
            <CountryDropdown
              value={info.country || ""}
              label="Country / Region"
              error={infoError.country}
              onChange={(value) =>
                handleInput(value, "country", setInfo, setInfoError, schema)
              }
            />
          </div>
          <FullWidth>
            <InputArea
              value={info.bio || ""}
              label="Professional Summary / Bio"
              width="100%"
              height="150px"
              onChange={(val) =>
                handleInput(val, "bio", setInfo, setInfoError, schema)
              }
              error={infoError.bio}
            />
          </FullWidth>
        </Grid>
      </div>

      <div>
        <SectionTitle>Links & Socials</SectionTitle>
        <p
          style={{
            color: "var(--text-color-secondary, #6b7280)",
            marginBottom: "1.5rem",
            fontSize: "0.9rem",
          }}
        >
          Add your professional links like LinkedIn, GitHub, or your personal
          portfolio.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {info.links.map((link, index) => (
            <LinkRow key={index}>
              <div style={{ flex: 1 }}>
                <Input
                  value={link.title}
                  label="Platform (e.g. LinkedIn)"
                  width="100%"
                  onChange={(val) => handleLinkChange(index, "title", val)}
                  error={infoError.links?.[index]?.title}
                />
              </div>
              <div style={{ flex: 2 }}>
                <Input
                  value={link.url}
                  label="URL"
                  width="100%"
                  onChange={(val) => handleLinkChange(index, "url", val)}
                  error={infoError.links?.[index]?.url}
                />
              </div>
              <Button
                icon="pi pi-trash"
                severity="danger"
                outlined
                aria-label="Remove Link"
                onClick={() => removeLink(index)}
                style={{ alignSelf: "flex-end", marginBottom: "0.2rem" }}
              />
            </LinkRow>
          ))}
        </div>

        <Button
          icon="pi pi-plus"
          label="Add another link"
          onClick={addLink}
          text
          style={{ marginTop: "1rem" }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1rem",
          marginTop: "1.5rem",
        }}
      >
        {onBack && (
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            severity="secondary"
            onClick={onBack}
            disabled={loading}
          />
        )}
        <Button
          loading={loading}
          disabled={loading}
          label={editMode ? "Save Changes" : "Save & Continue"}
          icon="pi pi-check"
          onClick={handleSubmitData}
        />
      </div>
    </FormContainer>
  );
};

export default ProfileForm;
